<template>
  <div class="tickets-page">
    <div class="content">
      <div class="page-header">
        <h1 class="page-title">My Tickets</h1>
        <div class="sort-wrap">
          <label class="sort-label">Sort by:</label>
          <select v-model="sortBy" class="sort-select">
            <option value="date">Date</option>
            <option value="name">Name</option>
          </select>
        </div>
      </div>

      <div class="tabs">
        <button class="tab" :class="{ 'tab--active': activeTab === 'upcoming' }" @click="activeTab = 'upcoming'">
          Upcoming
        </button>
        <button class="tab" :class="{ 'tab--active': activeTab === 'past' }" @click="activeTab = 'past'">
          Past
        </button>
        <button class="tab" :class="{ 'tab--active': activeTab === 'gifts' }" @click="selectGiftsTab">
          Gifts
        </button>
      </div>

      <!-- Gifts tab -->
      <template v-if="activeTab === 'gifts'">
        <div v-if="giftsLoading" class="state-msg">Loading your gifts…</div>
        <div v-else-if="giftsError" class="state-msg state-msg--error">{{ giftsError }}</div>
        <div v-else-if="!gifts.length" class="state-msg">
          You haven’t sent any tickets as gifts yet.
          <router-link to="/events" class="link">Browse events →</router-link>
        </div>
        <div v-else class="event-list">
          <div v-for="gift in gifts" :key="gift.orderId" class="event-card">
            <div class="event-card__header event-card__header--static">
              <div class="event-card__img-wrap">
                <img v-if="gift.eventImageUrl" :src="gift.eventImageUrl" class="event-card__img" alt="" />
                <div v-else class="event-card__img event-card__img--placeholder"></div>
              </div>
              <div class="event-card__body">
                <div class="event-card__meta-row">
                  <span class="event-card__date">{{ formatDateRange(gift.eventDate) }}</span>
                  <span
                    class="gift-status-pill"
                    :class="'gift-status-pill--' + gift.giftStatus"
                    data-testid="gift-status"
                  >{{ giftStatusLabel(gift.giftStatus) }}</span>
                </div>
                <p class="event-card__title">{{ gift.eventTitle }}</p>
                <p class="event-card__venue">
                  For {{ gift.recipientName }} · {{ gift.recipientEmail }}
                </p>
              </div>
            </div>
            <div class="ticket-list">
              <div class="ticket-row" v-for="(t, i) in gift.tiers" :key="i">
                <div class="ticket-row__info">
                  <span class="ticket-row__tier">{{ t.quantity }} × {{ t.name }}</span>
                </div>
              </div>
              <div class="ticket-row">
                <div class="ticket-row__info">
                  <span class="ticket-row__id">Order #{{ gift.orderId }}</span>
                </div>
                <span class="gift-timeline">
                  <span class="gift-timeline__step gift-timeline__step--done">Sent</span>
                  <span class="gift-timeline__step" :class="giftStepClass(gift, 'awaiting_claim')">Waiting to be claimed</span>
                  <span class="gift-timeline__step" :class="giftStepClass(gift, 'claimed')">Claimed</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template v-else>
      <div v-if="loading" class="state-msg">Loading your tickets…</div>
      <div v-else-if="error" class="state-msg state-msg--error">{{ error }}</div>
      <div v-else-if="!sortedBuckets.length" class="state-msg">
        You don’t have any tickets here yet.
        <router-link to="/events" class="link">Browse events →</router-link>
      </div>

      <div v-else class="event-list">
        <div
          v-for="bucket in sortedBuckets"
          :key="bucket.eventId"
          class="event-card"
        >
          <!-- Event header -->
          <div class="event-card__header" @click="toggleBucket(bucket.eventId)">
            <div class="event-card__img-wrap">
              <img v-if="bucket.eventImageUrl" :src="bucket.eventImageUrl" class="event-card__img" alt="" />
              <div v-else class="event-card__img event-card__img--placeholder"></div>
            </div>
            <div class="event-card__body">
              <div class="event-card__meta-row">
                <span class="event-card__date">{{ formatDateRange(bucket.eventDate, bucket.eventEndDate) }}</span>
                <span class="event-card__count">{{ bucket.tickets.length }} {{ bucket.tickets.length === 1 ? 'Ticket' : 'Tickets' }}</span>
              </div>
              <p class="event-card__title">{{ bucket.eventTitle }}</p>
              <p v-if="bucket.eventVenue" class="event-card__venue">{{ bucket.eventVenue }}</p>
            </div>
            <button
              v-if="canBuyMore(bucket.eventId)"
              class="event-card__buy-more-btn"
              @click.stop="buyMoreTickets(bucket.eventId)"
            >Buy More Tickets</button>
            <span class="event-card__chevron" :class="{ 'event-card__chevron--open': expandedEvents.includes(bucket.eventId) }">›</span>
          </div>

          <!-- Individual tickets -->
          <div v-if="expandedEvents.includes(bucket.eventId)" class="ticket-list">
            <div v-for="ticket in bucket.tickets" :key="ticket.id" class="ticket-row">
              <div class="ticket-row__main">
                <router-link
                  v-if="hasOwnNftArt(ticket)"
                  :to="`/tickets/${ticket.id}`"
                  class="ticket-row__nft"
                >
                  <img :src="ticket.nftImageUrl" :alt="ticket.nftRider ? `${ticket.nftRider} rider card` : 'NFT artwork'" loading="lazy" />
                </router-link>
                <div class="ticket-row__info">
                  <span class="ticket-row__tier">{{ ticket.tierName }}</span>
                  <span v-if="ticket.nftRider" class="ticket-row__rider">{{ ticket.nftRider }}</span>
                  <span class="ticket-row__id">#{{ ticket.id }}</span>
                  <span
                    class="ticket-row__status"
                    :class="isUsed(ticket) ? 'ticket-row__status--used' : 'ticket-row__status--valid'"
                  >{{ isUsed(ticket) ? 'Used' : 'Not used' }}</span>
                  <span class="ticket-row__status ticket-row__status--no-transfer">Not Transferrable</span>
                  <a
                    v-if="ticket.explorerUrl"
                    :href="ticket.explorerUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="ticket-row__explorer-link"
                    @click.stop
                  >View on blockchain ↗</a>
                </div>
              </div>
              <button class="ticket-row__qr-btn" @click.stop="openQr(ticket)">Show QR code</button>
            </div>
          </div>
        </div>
      </div>
      </template>
    </div>

    <QrModal
      v-if="qrTicket"
      :qr-base64="qrData.qrBase64"
      :qr-code="qrData.qrCode"
      :ticket-label="qrTicketLabel"
      :used="qrTicket.status === 'checked_in'"
      @close="qrTicket = null; qrData = {}"
    />
  </div>
</template>

<script>
import ticketingApi from '@/api/ticketing'

export default {
  name: 'TicketsIndex',
  components: { QrModal: () => import('@/components/events/QrModal.vue') },
  data() {
    return {
      tickets: [],
      loading: true,
      error: null,
      activeTab: 'upcoming',
      sortBy: 'date',
      expandedEvents: [],
      qrTicket: null,
      qrData: {},
      gifts: [],
      giftsLoading: false,
      giftsLoaded: false,
      giftsError: null,
      eventAvailability: {},
    }
  },
  computed: {
    eventBuckets() {
      const map = new Map()
      for (const t of this.tickets) {
        if (!map.has(t.eventId)) {
          map.set(t.eventId, {
            eventId: t.eventId,
            eventTitle: t.eventTitle,
            eventDate: t.eventDate,
            eventEndDate: t.eventEndDate,
            eventVenue: t.eventVenue,
            eventImageUrl: t.eventImageUrl,
            tickets: [],
          })
        }
        map.get(t.eventId).tickets.push(t)
      }
      return Array.from(map.values())
    },
    filteredBuckets() {
      const now = Date.now()
      return this.eventBuckets.filter((b) => {
        const end = b.eventEndDate ? new Date(b.eventEndDate) : new Date(b.eventDate)
        return this.activeTab === 'upcoming' ? end >= now : end < now
      })
    },
    sortedBuckets() {
      return [...this.filteredBuckets].sort((a, b) => {
        if (this.sortBy === 'name') return a.eventTitle.localeCompare(b.eventTitle)
        return new Date(a.eventDate) - new Date(b.eventDate)
      })
    },
    qrTicketLabel() {
      if (!this.qrTicket) return null
      return `${this.qrTicket.tierName} — Ticket #${this.qrTicket.id}`
    },
  },
  async created() {
    if (this.$route.query.tab === 'gifts') this.activeTab = 'gifts'
    try {
      const res = await ticketingApi.getMyTickets()
      this.tickets = res.data || res
      this.checkEventAvailability()
    } catch {
      this.error = 'We couldn’t load your tickets. Please try again.'
    } finally {
      this.loading = false
    }
    if (this.activeTab === 'gifts') this.loadGifts()
  },
  methods: {
    // Mirrors the scanner: a checked-out ticket can't get back in unless the
    // event allows re-entry, so it's spent rather than "Not used".
    isUsed(ticket) {
      return ticket.status === 'checked_in' ||
        (ticket.status === 'checked_out' && !ticket.eventAllowReEntry)
    },
    // A bucketed event can still take new orders when its sale window is open
    // and at least one tier has inventory left — same rule the event detail
    // page uses to enable "Get tickets".
    async checkEventAvailability() {
      const eventIds = [...new Set(this.tickets.map((t) => t.eventId))]
      await Promise.all(eventIds.map(async (eventId) => {
        try {
          const res = await ticketingApi.getEvent(eventId)
          const event = res.data || res
          const now = Date.now()
          const start = event.saleStart ? new Date(event.saleStart).getTime() : null
          const end = event.saleEnd ? new Date(event.saleEnd).getTime() : null
          const saleOpen = !(start && now < start) && !(end && now > end)
          const hasStock = (event.tiers || []).some((t) => t.quantitySold < t.quantityTotal)
          this.$set(this.eventAvailability, eventId, saleOpen && hasStock)
        } catch {
          // Non-fatal — just don't show the button for this event.
        }
      }))
    },
    // Tiers without their own NFT art fall back to the event hero image, which
    // the event header already shows — only render art that's the ticket's own.
    hasOwnNftArt(ticket) {
      return !!ticket.nftImageUrl && ticket.nftImageUrl !== ticket.eventImageUrl
    },
    canBuyMore(eventId) {
      return this.eventAvailability[eventId] === true
    },
    buyMoreTickets(eventId) {
      this.$router.push(`/events/${eventId}`)
    },
    selectGiftsTab() {
      this.activeTab = 'gifts'
      this.loadGifts()
    },
    async loadGifts() {
      if (this.giftsLoaded || this.giftsLoading) return
      this.giftsLoading = true
      try {
        const res = await ticketingApi.listMyGifts()
        this.gifts = res.data || res
        this.giftsLoaded = true
      } catch {
        this.giftsError = 'We couldn’t load your gifts. Please try again.'
      } finally {
        this.giftsLoading = false
      }
    },
    giftStatusLabel(status) {
      return { awaiting_claim: 'Waiting to be claimed', claimed: 'Claimed' }[status] || 'Sent'
    },
    giftStepClass(gift, step) {
      if (gift.giftStatus === 'claimed') return 'gift-timeline__step--done'
      if (step === 'awaiting_claim') return 'gift-timeline__step--current'
      return ''
    },
    toggleBucket(eventId) {
      const idx = this.expandedEvents.indexOf(eventId)
      if (idx === -1) this.expandedEvents.push(eventId)
      else this.expandedEvents.splice(idx, 1)
    },
    async openQr(ticket) {
      this.qrTicket = ticket
      this.qrData = {}
      try {
        const res = await ticketingApi.generateQr(ticket.id)
        this.qrData = res.data || res
      } catch {
        this.qrTicket = null
        alert('We couldn’t load your QR code. Please try again.')
      }
    },
    formatDateRange(start, end) {
      if (!start) return ''
      const opts = { month: 'short', day: 'numeric', year: 'numeric' }
      const s = new Date(start).toLocaleDateString(undefined, opts)
      if (!end) return s
      const e = new Date(end).toLocaleDateString(undefined, opts)
      return `${s} – ${e}`
    },
  },
}
</script>

<style scoped lang="scss">
$bg: #f0f0f5;
$surface: #ffffff;
$text: #0a0a0a;
$muted: rgba(10,10,10,0.5);
$purple-pill: linear-gradient(135deg, #c4b5fd 0%, #f9a8d4 100%);
$radius-lg: 20px;
$radius-pill: 44px;
$page-px: 80px;
$font: 'Inter', sans-serif;

* { box-sizing: border-box; }

.tickets-page {
  font-family: $font;
  background: $bg;
  min-height: 100vh;
  color: $text;
}

.content {
  padding: 48px $page-px;

  @media (max-width: 1080px) { padding: 32px 32px; }
  @media (max-width: 640px)  { padding: 24px 16px; }
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 12px;
}

.page-title {
  font-size: 32px;
  font-weight: 800;
  margin: 0;

  @media (max-width: 640px) { font-size: 24px; }
}

.sort-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sort-label { font-size: 14px; color: $muted; }

.sort-select {
  font-size: 14px;
  font-weight: 700;
  border: 1px solid rgba(0,0,0,0.15);
  border-radius: $radius-pill;
  padding: 6px 14px;
  background: $surface;
  cursor: pointer;
  outline: none;
}

.tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 28px;
  border-bottom: 2px solid rgba(0,0,0,0.08);
}

.tab {
  font-size: 15px;
  font-weight: 700;
  padding: 10px 20px;
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  margin-bottom: -2px;
  cursor: pointer;
  color: $muted;
  transition: color 0.15s, border-color 0.15s;

  &--active {
    color: $text;
    border-bottom-color: $text;
  }
}

.state-msg {
  font-size: 18px;
  color: $muted;
  padding: 40px 0;

  &--error { color: #c0392b; }

  .link {
    color: $text;
    font-weight: 700;
    margin-left: 8px;
    text-decoration: none;
  }
}

.event-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.event-card {
  background: $surface;
  border-radius: $radius-lg;
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px 20px;
    cursor: pointer;

    &:hover { background: rgba(0,0,0,0.02); }

    &--static {
      cursor: default;
      &:hover { background: none; }
    }
  }

  &__img-wrap {
    width: 72px;
    height: 72px;
    border-radius: 12px;
    overflow: hidden;
    flex-shrink: 0;
  }

  &__img {
    width: 100%;
    height: 100%;
    object-fit: cover;

    &--placeholder {
      background: linear-gradient(135deg, #e8e0ff 0%, #fce4f0 100%);
    }
  }

  &__body { flex: 1; min-width: 0; }

  &__meta-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
    flex-wrap: wrap;
  }

  &__date {
    font-size: 12px;
    color: $muted;
    font-weight: 600;
  }

  &__count {
    font-size: 12px;
    font-weight: 700;
    background: $purple-pill;
    padding: 2px 10px;
    border-radius: $radius-pill;
    color: $text;
  }

  &__title {
    font-size: 15px;
    font-weight: 800;
    margin: 0 0 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__venue {
    font-size: 13px;
    color: $muted;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__chevron {
    font-size: 22px;
    color: $muted;
    transition: transform 0.2s;
    flex-shrink: 0;

    &--open { transform: rotate(90deg); }
  }

  &__buy-more-btn {
    flex-shrink: 0;
    background: $purple-pill;
    color: $text;
    border: none;
    border-radius: $radius-pill;
    padding: 10px 20px;
    font-size: 13px;
    font-weight: 700;
    font-family: $font;
    cursor: pointer;
    white-space: nowrap;
    transition: opacity 0.15s;

    &:hover { opacity: 0.85; }

    @media (max-width: 640px) { padding: 8px 14px; font-size: 12px; }
  }
}

.ticket-list {
  border-top: 1px solid rgba(0,0,0,0.07);
}

.ticket-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid rgba(0,0,0,0.05);
  gap: 12px;

  &:last-child { border-bottom: none; }

  &__main {
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
  }

  &__nft {
    flex-shrink: 0;
    width: 56px;
    aspect-ratio: 4 / 5;
    border-radius: 8px;
    overflow: hidden;
    background: #f0f0f5;
    box-shadow: 0 2px 6px rgba(0,0,0,0.12);
    transition: transform 0.15s;

    &:hover { transform: scale(1.04); }

    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__rider {
    font-size: 13px;
    font-weight: 600;
    color: $text;
  }

  &__info {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  &__tier {
    font-size: 14px;
    font-weight: 700;
    color: $text;
  }

  &__id {
    font-size: 13px;
    color: $muted;
    font-family: monospace;
  }

  &__status {
    font-size: 12px;
    font-weight: 700;
    padding: 2px 10px;
    border-radius: $radius-pill;

    &--valid  { background: #d4edda; color: #155724; }
    &--used   { background: #fff3cd; color: #856404; }
    &--no-transfer { background: #f8d7da; color: #721c24; }
  }

  &__explorer-link {
    font-size: 12px;
    font-weight: 700;
    color: #6d28d9;
    text-decoration: none;

    &:hover { text-decoration: underline; }
  }

  &__qr-btn {
    flex-shrink: 0;
    background: $text;
    color: #fff;
    border: none;
    border-radius: $radius-pill;
    padding: 8px 18px;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    white-space: nowrap;

    &:hover { opacity: 0.85; }
  }
}

.gift-status-pill {
  font-size: 12px;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: $radius-pill;
  background: #fff3cd;
  color: #856404;

  &--claimed { background: #d4edda; color: #155724; }
}

.gift-timeline {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;

  &__step {
    font-size: 11px;
    font-weight: 700;
    padding: 2px 10px;
    border-radius: $radius-pill;
    background: #f0f0f5;
    color: $muted;

    &--current { background: #ede9fe; color: #5b21b6; }
    &--done    { background: #d4edda; color: #155724; }
  }
}
</style>
